import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import ConfidenceSelector from "../components/ConfidenceSelector";
import { StageLoading, ErrorState } from "../components/Loading";
import { C } from "../lib/theme";
import { ArrowRight, Check, X, Copy, RotateCcw, PartyPopper } from "lucide-react";
import { renderNotes } from "../lib/markdown";
import { toast } from "sonner";

const KIND_LABEL = { mcq: "Scenario", short: "Short answer", spotMistake: "Spot the mistake" };

export default function Stage3({ session, onReview, onMastered }) {
  const [questions, setQuestions] = useState(null);
  const [err, setErr] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [confidence, setConfidence] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setErr(false);
    try {
      const r = await api.stage3Generate(session.id);
      setQuestions(r.questions);
      setAnswers(Array(r.questions.length).fill(null));
      setConfidence(Array(r.questions.length).fill(null));
    } catch (e) {
      setErr(true);
    }
  }, [session.id]);

  const started = useRef(false);
  useEffect(() => { if (started.current) return; started.current = true; load(); }, [load]);

  if (err) return <ErrorState message="Couldn't build the test." onRetry={load} />;
  if (result) return <Results result={result} session={session} onReview={onReview} onMastered={onMastered} />;
  if (!questions) return <StageLoading color={C.grn} title="Writing your final test…" sub="Fresh questions weighted toward your shaky spots." />;
  if (submitting) return <StageLoading color={C.grn} title="Grading your answers…" sub="The short answers are graded against a rubric." />;

  const q = questions[idx];
  const isLast = idx === questions.length - 1;
  const ans = answers[idx];
  const canNext = (q.kind === "mcq" ? ans !== null : (typeof ans === "string" && ans.trim().length > 0)) && confidence[idx] !== null;

  const setAns = (v) => setAnswers((a) => { const n = [...a]; n[idx] = v; return n; });
  const setConf = (v) => setConfidence((c) => { const n = [...c]; n[idx] = v; return n; });

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.stage3Submit(session.id, answers, confidence);
      setResult(r);
    } catch (e) {
      toast.error("Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="stage3">
      <div className="mb-1 flex items-center justify-between font-mono text-xs" style={{ color: C.grn }}>
        <span>Stage 3 · Prove {session.attempt > 1 && `· Attempt ${session.attempt}`}</span>
        <span>{idx + 1}/{questions.length}</span>
      </div>
      <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${((idx + 1) / questions.length) * 100}%`, background: C.grn }} />
      </div>

      <div key={idx} className="anim-fade-up">
        <div className="font-mono text-[11px] uppercase tracking-wider text-muted">{KIND_LABEL[q.kind]}</div>
        <p className="mt-2 text-lg text-ink">{q.prompt}</p>

        {q.kind === "mcq" && (
          <div className="mt-4 space-y-2">
            {q.options.map((o, i) => {
              const active = ans === i;
              return (
                <button
                  key={i}
                  data-testid={`stage3-opt-${i}`}
                  onClick={() => setAns(i)}
                  className="flex w-full items-center gap-3 rounded-md border px-3 py-3 text-left text-sm transition-colors"
                  style={{ borderColor: active ? C.grn : "rgba(255,255,255,0.08)", background: active ? `${C.grn}18` : "transparent" }}
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded font-mono text-xs" style={{ background: active ? C.grn : "rgba(255,255,255,0.06)", color: active ? "#0B0D12" : "#8B93A5" }}>
                    {["A", "B", "C", "D"][i]}
                  </span>
                  {o}
                </button>
              );
            })}
          </div>
        )}

        {(q.kind === "short" || q.kind === "spotMistake") && (
          <textarea
            data-testid="stage3-text-input"
            value={ans || ""}
            onChange={(e) => setAns(e.target.value)}
            rows={q.kind === "short" ? 4 : 3}
            placeholder={q.kind === "spotMistake" ? "Name the error and correct it." : "2–4 sentences."}
            className="mt-4 w-full resize-none rounded-lg border border-hair bg-surface2 px-3 py-3 text-sm text-ink placeholder:text-muted focus:border-grn/60 focus:outline-none"
          />
        )}

        <ConfidenceSelector value={confidence[idx]} onChange={setConf} idPrefix="stage3-conf" />

        <div className="mt-6 flex items-center justify-between">
          <button
            data-testid="stage3-back-btn"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            className="rounded-full border border-hair px-4 py-2.5 text-sm text-muted transition-colors hover:text-ink disabled:opacity-30"
          >
            Back
          </button>
          <button
            data-testid="stage3-next-btn"
            onClick={() => (isLast ? submit() : setIdx((i) => i + 1))}
            disabled={!canNext}
            className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-bg transition-all disabled:opacity-40"
            style={{ background: C.grn }}
          >
            {isLast ? "Submit test" : "Next"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Results({ result, session, onReview, onMastered }) {
  const { score, passed, baseline, calibration, review, missedConcepts, studyCard } = result;

  const copyCard = () => {
    const text = `${session.topic}\n\n${studyCard?.summary || ""}\n\nMy notes:\n${studyCard?.notes || ""}`;
    navigator.clipboard.writeText(text);
    toast.success("Study card copied");
  };

  return (
    <div className="mx-auto max-w-2xl anim-fade-up" data-testid="stage3-results">
      <div className="flex items-center gap-4">
        <div>
          <div className="font-mono text-xs" style={{ color: passed ? C.grn : C.ros }}>{passed ? "Mastered" : "Not yet"}</div>
          <div className="font-display text-6xl" style={{ color: passed ? C.grn : C.ros }}>{score}%</div>
        </div>
        {passed && (
          <div className="flex h-14 w-14 items-center justify-center rounded-full glow-green" style={{ background: `${C.grn}22` }} data-testid="mastered-check">
            <Check className="h-8 w-8" style={{ color: C.grn }} />
          </div>
        )}
      </div>

      {/* before/after */}
      <div className="mt-6 rounded-lg border border-hair bg-surface p-5">
        <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-muted">Before → After</div>
        <div className="flex items-end justify-center gap-10" style={{ height: 150 }}>
          {[{ label: "Baseline", v: baseline, c: C.vio }, { label: "Result", v: score, c: C.grn }].map((b) => (
            <div key={b.label} className="flex h-full flex-1 flex-col items-center justify-end" style={{ maxWidth: 110 }}>
              <span className="mb-1 font-mono text-sm" style={{ color: b.c }}>{b.v}%</span>
              <div className="w-full rounded-t-md transition-all duration-700" style={{ height: `${Math.max(6, b.v * 1.1)}px`, background: b.c }} />
              <span className="mt-2 font-mono text-[11px] text-muted">{b.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* calibration */}
      <div className="mt-4 rounded-lg border border-hair bg-surface p-5" data-testid="calibration">
        <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted">Calibration</div>
        <div className="flex flex-wrap gap-4 text-sm">
          <span style={{ color: C.grn }}>Confident and right: {calibration.confidentRight}</span>
          <span className="rounded px-2" style={{ color: C.ros, boxShadow: `inset 0 0 0 1px ${C.ros}` }}>Confident but wrong: {calibration.confidentWrong}</span>
          <span style={{ color: C.amb }}>Unsure but right: {calibration.unsureRight}</span>
        </div>
        {calibration.confidentWrong > 0 && (
          <p className="mt-2 text-xs text-ros/90">The confident-but-wrong ones are the dangerous gaps — you thought you knew them.</p>
        )}
      </div>

      {/* per-question review */}
      <div className="mt-4 space-y-2">
        {review.map((r, i) => (
          <div
            key={r.id}
            data-testid={`review-${i}`}
            className="rounded-md border p-3 text-sm"
            style={{ borderColor: r.correct ? C.grn : C.ros, background: `${r.correct ? C.grn : C.ros}0d`, boxShadow: r.confidentButWrong ? `0 0 0 1px ${C.ros}` : "none" }}
          >
            <div className="flex items-start gap-2">
              {r.correct ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-grn" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-ros" />}
              <div>
                <div className="text-ink/90">{r.prompt}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {r.confidentButWrong && <span className="rounded px-1.5 font-mono text-[9px]" style={{ background: `${C.ros}22`, color: C.ros }}>confident but wrong</span>}
                  {r.guessedRight && <span className="rounded px-1.5 font-mono text-[9px]" style={{ background: `${C.amb}22`, color: C.amb }}>guessed right · counts less</span>}
                </div>
                {r.feedback && <div className="mt-1 text-xs text-muted">{r.feedback}</div>}
                <div className="mt-1 text-xs text-muted">{r.explanation}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* outcome actions */}
      {passed ? (
        <div className="mt-6">
          <div className="rounded-lg border border-hair bg-surface p-5" style={{ borderTop: `2px solid ${C.grn}` }} data-testid="study-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-display text-lg text-ink"><PartyPopper className="h-4 w-4" style={{ color: C.grn }} /> Study card</div>
              <button onClick={copyCard} data-testid="copy-card-btn" className="flex items-center gap-1.5 rounded-full border border-hair px-3 py-1 text-xs text-muted hover:text-ink"><Copy className="h-3 w-3" /> Copy</button>
            </div>
            <p className="mt-2 text-sm text-ink/90">{studyCard?.summary}</p>
            {studyCard?.notes?.trim() && (
              <div className="mt-3 border-t border-hair pt-3">
                <div className="font-mono text-[10px] uppercase tracking-wider text-muted">Your notes</div>
                <div className="mt-1 text-sm text-ink/80" dangerouslySetInnerHTML={{ __html: renderNotes(studyCard.notes) }} />
              </div>
            )}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <NextTopics title="Go deeper" items={studyCard?.goDeeper} color={C.vio} />
            <NextTopics title="Related" items={studyCard?.related} color={C.blu} />
          </div>
          <button onClick={onMastered} className="mt-6 rounded-full border border-hair px-5 py-2.5 text-sm text-ink hover:bg-white/5" data-testid="back-home-btn">
            Back to home
          </button>
        </div>
      ) : (
        <div className="mt-6">
          <div className="rounded-lg border p-5" style={{ borderColor: C.ros, background: `${C.ros}0d` }} data-testid="not-yet">
            <div className="font-display text-lg" style={{ color: C.ros }}>Concepts to review</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {missedConcepts.map((m, i) => (
                <span key={i} className="rounded-full border px-3 py-1 text-xs" style={{ borderColor: C.ros, color: C.ros }}>{m}</span>
              ))}
            </div>
          </div>
          <button
            data-testid="review-again-btn"
            onClick={onReview}
            className="mt-6 flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-bg transition-transform hover:scale-[1.03]"
            style={{ background: C.blu }}
          >
            <RotateCcw className="h-4 w-4" /> Review and try again
          </button>
          <p className="mt-2 text-xs text-muted">You'll go back to Learn with a focused review of exactly these concepts — never back to the start.</p>
        </div>
      )}
    </div>
  );
}

function NextTopics({ title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-hair bg-surface p-4">
      <div className="font-mono text-[11px] uppercase tracking-wider" style={{ color }}>{title}</div>
      <div className="mt-2 space-y-1">
        {items.map((t, i) => (
          <a key={i} href={`/?topic=${encodeURIComponent(t)}`} className="block text-sm text-ink/80 hover:text-ink">→ {t}</a>
        ))}
      </div>
    </div>
  );
}
